import { useState, useCallback, useLayoutEffect } from 'react';

// Positions a dropdown menu with `position: fixed` so it escapes clipping by
// scroll/overflow ancestors (e.g. the AgentPopup modal body). The menu height
// is capped to the space actually available in the viewport, flipping upward
// when the trigger sits too close to the bottom. Same positioning approach as
// BusinessContextSelector.
//
// Returns a style object for the menu element, or null while closed / before
// the first layout pass. Visuals (border, shadow, background) stay at the
// call site — this hook only positions.
//
// Options (all default to the original behaviour, so existing callers are
// untouched): `align: 'left'` pins the menu's left edge to the trigger's;
// `align: 'right'` hangs it from the trigger's RIGHT edge (an icon button at
// the end of a row, e.g. a ⋮ actions menu). `matchWidth` copies the trigger's
// width — right for a select-style dropdown, wrong for a menu under a 28px
// icon, which keeps its own intrinsic width when this is false.
export default function useFixedDropdownPosition(triggerRef, open, dropH = 240, { align = 'left', matchWidth = true } = {}) {
  const [style, setStyle] = useState(null);

  const calc = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < dropH && spaceAbove > spaceBelow;
    setStyle({
      position: 'fixed',
      ...(align === 'right'
        ? { right: window.innerWidth - rect.right, left: 'auto' }
        : { left: rect.left, right: 'auto' }),
      ...(matchWidth ? { width: rect.width } : {}),
      zIndex: 9999,
      maxHeight: `${Math.min(dropH, (openUpward ? spaceAbove : spaceBelow) - 8)}px`,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
        : { top: rect.bottom + 4, bottom: 'auto' }),
    });
  }, [triggerRef, dropH, align, matchWidth]);

  // Recalculate when opening, and follow the trigger on scroll/resize. The
  // scroll listener is capture-phase because scroll events don't bubble —
  // this is how scrolls of the modal body (not just the window) are caught.
  useLayoutEffect(() => {
    if (!open) { setStyle(null); return undefined; }
    calc();
    window.addEventListener('scroll', calc, true);
    window.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('scroll', calc, true);
      window.removeEventListener('resize', calc);
    };
  }, [open, calc]);

  return style;
}